import { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';

import useQuestionnaire from 'hooks/useQuestionnaire';
import {
  ENGLISH_OTHER,
  ENGLISH_SPECIFIC_DIETARY_REGIMEN,
  SPANISH_OTHER,
  SPANISH_SPECIFIC_DIETARY_REGIMEN,
} from 'lib/constants';
import { checkSpanishLocale, cn, isValidExpression } from 'lib/utils';
import { QuestionnaireParams } from 'services/profile/type';
import { useQuestionnaireStore } from 'store/questionnaire';
import Checkmark from 'pages/profile/questionnaire/components/Checkmark';
import { Input } from 'components/ui/input';
import QuestionnaireContainer from './components/QuestionnaireContainer';

interface HabitItemProps {
  item: any;
  selectedHabit: string[];
  onSelect: (vaue: string) => void;
  selectedOptions: number[];
  setSelectedOptions: (value: number[]) => void;
  otherText: string;
  setOtherText: (value: string) => void;
  questionData: QuestionnaireParams;
}

export default function EatingHabits() {
  const questionData = useQuestionnaireStore((store) => store.questionData);

  const isSpanish = checkSpanishLocale();

  const { onSubmit, isLoading } = useQuestionnaire();

  const [selectedHabit, setSelectedHabit] = useState<string[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<number[]>([]);
  const [otherText, setOtherText] = useState('');

  const englishChoices = JSON.parse(questionData?.eng_choices || '{}');
  const spanishChoices = JSON.parse(questionData?.spanish_choices || '{}');

  const eatingHabits: { en: string; es: string }[] = useMemo(
    () =>
      englishChoices?.length
        ? englishChoices.map((choice: string, index: number) => ({
            en: choice,
            es: spanishChoices[index],
          }))
        : [],
    [englishChoices, spanishChoices]
  );

  const habits = useMemo(
    () =>
      eatingHabits?.length
        ? eatingHabits.map((habit) => habit?.[isSpanish ? 'es' : 'en'])
        : [],
    [eatingHabits, isSpanish]
  );

  useEffect(() => {
    if (questionData?.user_eng_choices || questionData?.user_spanish_choices) {
      const parsedAnswer = isValidExpression(
        questionData?.[
          isSpanish ? 'user_spanish_choices' : 'user_eng_choices'
        ] || ''
      )
        ? // eslint-disable-next-line no-eval
          eval(
            questionData?.[
              isSpanish ? 'user_spanish_choices' : 'user_eng_choices'
            ] || ''
          )
        : questionData?.[
            isSpanish ? 'user_spanish_choices' : 'user_eng_choices'
          ];
      const selected: string[] = [];
      parsedAnswer.forEach((ans: any) => {
        if (typeof ans === 'string') {
          selected.push(ans);
          return;
        }
        const [key, value] = Object.entries(ans)[0];
        selected.push(key);
        if ([ENGLISH_OTHER, SPANISH_OTHER].includes(key)) {
          setOtherText(typeof value === 'string' ? value : '');
        } else {
          const { curentOptions } = getDietaryOptions(questionData, isSpanish);
          const selectedIndex = (value as string[]).map((val) =>
            curentOptions.indexOf(val)
          );
          setSelectedOptions(selectedIndex);
        }
      });
      setSelectedHabit(selected);
    }
  }, [isSpanish, questionData]);

  const onHabitSelect = (habit: string) => {
    const newHabits = selectedHabit.includes(habit)
      ? selectedHabit.filter((h) => h !== habit)
      : [...selectedHabit, habit];
    setSelectedHabit(newHabits);
    if (!newHabits.includes(ENGLISH_SPECIFIC_DIETARY_REGIMEN)) {
      // setSelectedOptions([]);
    }
    if (
      !newHabits.includes(ENGLISH_OTHER) &&
      !newHabits.includes(SPANISH_OTHER)
    ) {
      setOtherText('');
    }
  };

  const onContinue = async () => {
    const otherLangHabits: any[] = [];
    const habit = [...selectedHabit].map((hab) => {
      if ([ENGLISH_OTHER, SPANISH_OTHER].includes(hab)) {
        otherLangHabits.push({
          [isSpanish ? ENGLISH_OTHER : SPANISH_OTHER]: otherText,
        });
        return { [hab]: otherText };
      }
      if (
        [
          ENGLISH_SPECIFIC_DIETARY_REGIMEN,
          SPANISH_SPECIFIC_DIETARY_REGIMEN,
        ].includes(hab)
      ) {
        const { curentOptions, otherOptions } = getDietaryOptions(
          questionData,
          isSpanish
        );
        const otherDietaries = otherOptions.filter(
          (opt: any, index: number) => {
            console.log(opt);
            return selectedOptions.includes(index);
          }
        );
        otherLangHabits.push({
          [isSpanish
            ? ENGLISH_SPECIFIC_DIETARY_REGIMEN
            : SPANISH_SPECIFIC_DIETARY_REGIMEN]: otherDietaries,
        });
        const currentDietaries = curentOptions.filter(
          (opt: any, index: number) => {
            console.log(opt);
            return selectedOptions.includes(index);
          }
        );
        return { [hab]: currentDietaries };
      }
      const otherLangHabitIndex = habits.findIndex((h) => h === hab);
      const otherHabits = eatingHabits.map((h) => h?.[isSpanish ? 'en' : 'es']);
      otherLangHabits.push(otherHabits[otherLangHabitIndex]);
      return hab;
    });

    onSubmit({
      choice_value: JSON.stringify(isSpanish ? otherLangHabits : habit),
      spanish_choice_value: JSON.stringify(isSpanish ? habit : otherLangHabits),
      questionData,
    });
  };

  return (
    <QuestionnaireContainer
      questionData={questionData}
      isLoading={isLoading}
      onContinue={onContinue}
      questionNumber={6}
    >
      <div className='grid grid-cols-2 gap-6'>
        {habits?.map((habit, index) => (
          <HabitItem
            item={habit}
            key={index.toString()}
            onSelect={onHabitSelect}
            selectedHabit={selectedHabit}
            selectedOptions={selectedOptions}
            setSelectedOptions={setSelectedOptions}
            otherText={otherText}
            setOtherText={setOtherText}
            questionData={questionData}
          />
        ))}
      </div>
    </QuestionnaireContainer>
  );
}
function HabitItem({
  item,
  selectedHabit,
  onSelect,
  selectedOptions,
  setSelectedOptions,
  otherText,
  setOtherText,
}: HabitItemProps) {
  const title = typeof item === 'string' ? item : Object.keys(item)[0];
  const isSelected = selectedHabit.includes(title);

  const options =
    typeof item === 'string' ? [] : Object.values(item).filter((val) => val);

  const onOptionSelect = (index: number) => {
    if (selectedOptions.includes(index)) {
      setSelectedOptions(selectedOptions.filter((sel) => sel !== index));
    } else {
      setSelectedOptions([...selectedOptions, index]);
    }
  };

  return (
    <div className='space-y-2'>
      <motion.div
        key={title}
        onClick={() => onSelect(title)}
        className={cn(
          'w-full border-[2px] rounded-2xl px-4 py-2 flex items-center justify-between cursor-pointer text-[#656D76] gap-4 transition-all duration-150',
          isSelected && 'border-primary text-primary bg-primary/10'
        )}
        whileTap={{ scale: 0.95 }}
        transition={{ duration: 0.15 }}
      >
        <p className='text-sm leading-5'>{title}</p>
        <Checkmark isSelected={isSelected} />
      </motion.div>

      <AnimatePresence>
        {isSelected && options?.length ? (
          <motion.div
            key='options-container'
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className='flex flex-wrap gap-2 overflow-hidden'
          >
            {(options[0] as string[]).map((option, index) => {
              const isOptionSelected = selectedOptions.includes(index);
              return (
                <motion.div
                  key={option}
                  className={cn(
                    'bg-[#F3F4F6] text-sm font-medium text-[#656D76] px-4 rounded-xl h-8 flex items-center justify-center cursor-pointer transition-all duration-150',
                    isOptionSelected && 'text-white bg-primary'
                  )}
                  whileTap={{ scale: 0.9 }}
                  transition={{ duration: 0.15 }}
                  onClick={() => onOptionSelect(index)}
                >
                  <p className='leading-5'>{option}</p>
                </motion.div>
              );
            })}
          </motion.div>
        ) : null}
      </AnimatePresence>

      {isSelected && [ENGLISH_OTHER, SPANISH_OTHER].includes(title) ? (
        <div>
          <Input
            placeholder='Enter eating habit...'
            value={otherText}
            onChange={(e) => setOtherText(e.target.value)}
            autoFocus
            className='text-sm'
          />
        </div>
      ) : null}
    </div>
  );
}

function getDietaryOptions(
  questionData: QuestionnaireParams,
  isSpanish: boolean
) {
  const spanishOptions = questionData?.spanish_choices;
  const parsedSpanishOptions = isValidExpression(spanishOptions || '')
    ? // eslint-disable-next-line no-eval
      eval(spanishOptions || '')
    : spanishOptions;
  const spanishDietaryOptions = parsedSpanishOptions?.find(
    (item: any) => item?.[SPANISH_SPECIFIC_DIETARY_REGIMEN]
  )?.[SPANISH_SPECIFIC_DIETARY_REGIMEN];
  const englishOptions = questionData?.eng_choices;
  const parsedEnglishOptions = isValidExpression(englishOptions || '')
    ? // eslint-disable-next-line no-eval
      eval(englishOptions || '')
    : englishOptions;
  const englishDietaryOptions = parsedEnglishOptions?.find(
    (item: any) => item?.[ENGLISH_SPECIFIC_DIETARY_REGIMEN]
  )?.[ENGLISH_SPECIFIC_DIETARY_REGIMEN];

  return {
    curentOptions: isSpanish ? spanishDietaryOptions : englishDietaryOptions,
    otherOptions: isSpanish ? englishDietaryOptions : spanishDietaryOptions,
  };
}
